import { useEffect, useState } from "react";
import { Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { XMarkIcon } from "@heroicons/react/24/outline";
import AdminPostCard from "./AdminPostCard";
import { hydrateSocialPost } from "../services/socialMonitoringService";
import { SocialPost } from "../types/socialPost";

interface PostPreviewModalProps {
  authorId: string;
  postId: string;
  onClose: () => void;
}

/**
 * Read-only post preview for the admin dashboard.
 * Renders the same visual PostCard shell as Social Monitoring (no interactions).
 */
const PostPreviewModal: React.FC<PostPreviewModalProps> = ({
  authorId,
  postId,
  onClose,
}) => {
  const [post, setPost] = useState<SocialPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        try {
          const hydrated = await hydrateSocialPost(authorId, postId);
          if (hydrated) {
            setPost(hydrated);
            return;
          }
        } catch (directErr) {
          console.warn(
            "Direct post hydration failed, falling back to callable:",
            directErr,
          );
        }

        const functions = getFunctions();
        const getPostForAdmin = httpsCallable(functions, "getPostForAdmin");
        const result = await getPostForAdmin({ authorId, postId });
        const data = result.data as {
          post: {
            id: string;
            notes: string | null;
            mediaList: string[];
            postTimestamp: number | null;
            duration: number | null;
            load: number | null;
            AverageHR: number | null;
            isPrivate: boolean;
          };
          author: { username: string | null; imageUrl: string | null };
        };

        setPost({
          id: data.post.id,
          userID: authorId,
          username: data.author.username || "",
          userImageUrl: data.author.imageUrl || "",
          postTimestamp: data.post.postTimestamp
            ? Timestamp.fromMillis(data.post.postTimestamp)
            : null,
          notes: data.post.notes || "",
          duration: data.post.duration ?? 0,
          mediaList: data.post.mediaList ?? [],
          load: data.post.load ?? undefined,
          AverageHR: data.post.AverageHR ?? undefined,
          isPrivate: data.post.isPrivate ?? false,
          likesList: [],
          CommentsList: [],
        });
      } catch (err: any) {
        console.error("Error fetching post for admin preview:", err);
        setError(err.message || "Failed to load post");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [authorId, postId]);

  return (
    <div
      className="fixed inset-0 z-[999999] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Post Preview</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
              Loading post...
            </div>
          ) : error ? (
            <div className="p-5">
              <div className="p-3 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg">
                {error}
              </div>
            </div>
          ) : post ? (
            <AdminPostCard post={post} embedded />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PostPreviewModal;
